import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useRef,
  ReactNode,
} from "react";
import { createUseStyles } from "$utils/createUseStyles";
import {
  createExpandedRowModel,
  createSortedRowModel,
  flexRender,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import ArrowUpSLineIcon from "remixicon-react/ArrowUpSLineIcon";
import { KeyboardArrowRight } from "@mui/icons-material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import { theme } from "$utils/theme/bataillog";
import { Button, Paper } from "$components";
import JDebounceInput from "../JDebounceInput/JDebounceInput";
import JLoader from "../JLoader/JLoader";
import usePersistedState from "$hooks/usePersistedState";
import RowsPerPage from "$components/RowsPerPage";

type CustomTableProps = {
  data: Array<any>;
  columns: Array<any>;
  fetchData?: () => void;
  isFetching?: boolean;
  pageCount?: number;
  pageIndex?: number;
  filters?: any;

  setFilter?: (name: string, value: any) => void;

  setPageIndex: (index: number) => void;
  renderRowSubComponent?: any;
  isSubTable?: boolean;
  hideFilters?: boolean;
  hideCheckboxes?: boolean;
  onClickAction?: any;
  setSelectedItem?: any;
  setSelectedRows?: any;
  selectedRows?: any[];
  rowProps?: (row: any) => any;
  hasActionRow?: boolean;
  actionRowNumber?: number;
  pageName?: string;
  noResultsText?: string;
  noDataText?: string;
  children?: ReactNode; // if there is some children, display them instead of the table body
  usePaper?: boolean;
  rowsPerPage?: number | null;
  setRowsPerPage?: (rows: number) => void;
};

// Déclaration statique des features TanStack Table utilisées par JTable :
// tri, expansion (sous-tableau custom, pas de vraies subRows), pagination
// manuelle (pilotée par le parent), sélection multi-lignes contrôlée.
const features = tableFeatures({
  rowSortingFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  sortedRowModel: createSortedRowModel(),
  expandedRowModel: createExpandedRowModel(),
});

const useCombinedRefs = (...refs: any[]): React.MutableRefObject<any> => {
  const targetRef = useRef(undefined);

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};

// Helper function to get row ID (supports both _id and id)
const getRowId = (row: any): string | number | undefined => {
  return row?._id ?? row?.id;
};

// Helper function to check if a row is selected by comparing _id or id
const isRowSelected = (row: any, selectedRows: any[]): boolean => {
  if (!row?.original || !selectedRows || selectedRows.length === 0) {
    return false;
  }
  const rowId = getRowId(row.original);
  if (!rowId) {
    // Fallback to reference equality if neither _id nor id is available
    return selectedRows.includes(row.original);
  }
  return selectedRows.some((selectedRow) => getRowId(selectedRow) === rowId);
};

// Adapte le helper métier ci-dessus à la signature attendue par l'option de
// table getRowId (doit toujours renvoyer une string) : repli sur l'index de
// ligne pour les lignes sans _id/id, comme le fait TanStack Table par défaut.
const tableGetRowId = (row: any, index: number): string => {
  const id = getRowId(row);
  return id !== undefined && id !== null ? String(id) : String(index);
};

// Traduit les définitions de colonnes "à la react-table v7" (Header/Cell/
// accessor + propriétés custom posées à plat) reçues des 14 consommateurs
// vers la forme attendue par TanStack Table v9 (header/cell/meta), sans
// toucher aux 14 fichiers appelants.
const normalizeColumns = (rawColumns: any[]) =>
  (rawColumns || []).map((col, index) => {
    const {
      Header,
      Cell,
      accessor,
      id: explicitId,
      filterName,
      filterComponent,
      customFormat,
      preventClickAction,
      className,
      name,
      hidden,
      minWidth,
      maxWidth,
      width,
      ...rest
    } = col;

    const id =
      explicitId ??
      (typeof accessor === "string" ? accessor : undefined) ??
      (typeof Header === "string" ? Header : undefined) ??
      `col-${index}`;

    const normalized: Record<string, any> = {
      id,
      header: Header,
      cell: Cell,
      meta: {
        filterName,
        filterComponent,
        customFormat,
        preventClickAction,
        className,
        name,
        hidden,
        minWidth,
        maxWidth,
        width,
        ...rest,
      },
    };

    // Aucun consommateur ne lit cell.value/getValue() (tous lisent
    // row.original directement dans leur Cell custom) : accessorFn ne sert
    // qu'à donner une valeur comparable au tri par défaut quand l'accessor
    // correspond à un vrai champ de row.original (ex: "type"), exactement
    // comme react-table v7 le faisait déjà pour ces colonnes.
    if (typeof accessor === "string") {
      normalized.accessorFn = (row: any) => row?.[accessor];
    }

    return normalized;
  });

interface IndeterminateCheckboxProps {
  indeterminate?: boolean;
  name?: string;
  disabled?: boolean;
  style?: any;
  row?: any;
  selectedRows?: any[];
  checked?: boolean;
  title?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
}
const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  IndeterminateCheckboxProps
>(
  (
    {
      indeterminate,
      disabled = false,
      style = {},
      selectedRows = [],
      row,
      ...rest
    },
    ref: React.Ref<HTMLInputElement>
  ) => {
    const defaultRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, defaultRef);

    useEffect(() => {
      if (combinedRef?.current) {
        combinedRef.current.indeterminate = indeterminate ?? false;
      }
    }, [combinedRef, indeterminate]);

    const overridenStyle = disabled ? { cursor: "default" } : style;

    const { checked } = rest;

    return (
      <input
        type="checkbox"
        ref={combinedRef}
        style={overridenStyle}
        disabled={disabled}
        // Toggle handled via onClick (fourni par TanStack Table, qui lit
        // event.target.checked pour décider de la nouvelle sélection) ;
        // onChange doit néanmoins être présent pour que React ne traite pas
        // le champ comme "lecture seule" et laisse le clic natif basculer
        // correctement le DOM à chaque fois.
        onChange={() => {}}
        {...rest}
        checked={
          row && selectedRows.length > 0
            ? isRowSelected(row, selectedRows)
            : checked
        }
      />
    );
  }
);

function JTable({
  data,
  columns,
  isFetching = false,
  pageCount: controlledPageCount,
  setFilter,
  filters,
  pageIndex,
  setPageIndex,
  renderRowSubComponent,
  isSubTable = false,
  hideFilters = false,
  hideCheckboxes = false,
  onClickAction = () => {},
  setSelectedItem,
  setSelectedRows,
  selectedRows = [],
  rowProps = () => {},
  hasActionRow = false,
  actionRowNumber = 1,
  pageName = "default",
  noResultsText = "Aucun résultat ne correspond à votre recherche",
  noDataText = "Aucune donnée n'a encore été créée",
  children,
  usePaper = true,
  rowsPerPage = null,
  setRowsPerPage = null,
}: CustomTableProps) {
  const normalizedColumns = useMemo(() => normalizeColumns(columns), [columns]);

  // selectedRows/rowProps changent de référence à (quasi) chaque rendu côté
  // consommateur. Les lire via une ref (mise à jour à chaque rendu, mais hors
  // des deps du useMemo ci-dessous) permet de garder `selectionColumn` — donc
  // `columns` passé à useTable — stable d'un rendu à l'autre : TanStack Table
  // reconstruit ses lignes/cellules internes quand la référence de `columns`
  // change, ce qui invalidait les handlers de sélection d'un clic sur l'autre.
  const selectedRowsRef = useRef(selectedRows);
  selectedRowsRef.current = selectedRows;
  const rowPropsRef = useRef(rowProps);
  rowPropsRef.current = rowProps;

  const selectionColumn = useMemo(
    () => ({
      id: "selection",
      header: !isSubTable
        ? ({ table }: any) => {
            const currentSelectedRows = selectedRowsRef.current;
            const visibleRowIds = new Set(
              table
                .getRowModel()
                .rows.map((row: any) => getRowId(row.original))
                .filter((id: any) => id)
            );
            const selectedIds = new Set(
              currentSelectedRows
                .map((row: any) => getRowId(row))
                .filter((id: any) => id)
            );
            const allSelected =
              visibleRowIds.size > 0 &&
              Array.from(visibleRowIds).every((id: string | number) =>
                selectedIds.has(id)
              );
            const someSelected =
              visibleRowIds.size > 0 &&
              Array.from(visibleRowIds).some((id: string | number) =>
                selectedIds.has(id)
              );

            return (
              <div>
                <IndeterminateCheckbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onClick={() => table.toggleAllRowsSelected(!allSelected)}
                  title="Toggle All Rows Selected"
                />
              </div>
            );
          }
        : () => null,
      meta: {
        width: 30,
        maxWidth: 30,
        minWidth: 30,
        preventClickAction: true,
      },
      cell: !isSubTable
        ? ({ row }: any) => {
            const { disabled } = rowPropsRef.current(row) ?? {};
            return (
              <div>
                <IndeterminateCheckbox
                  disabled={disabled}
                  selectedRows={selectedRowsRef.current}
                  row={row}
                  checked={row.getIsSelected()}
                  onClick={() => row.toggleSelected(!row.getIsSelected())}
                  title="Toggle Row Selected"
                />
              </div>
            );
          }
        : () => null,
    }),
    [isSubTable]
  );

  const finalColumns = useMemo(
    () =>
      hideCheckboxes
        ? normalizedColumns
        : [selectionColumn, ...normalizedColumns],
    [hideCheckboxes, selectionColumn, normalizedColumns]
  );

  // Etat de sélection dérivé de la prop externe selectedRows (source de
  // vérité), reconstruit à chaque rendu — remplace le stateReducer +
  // synchronisation asynchrone (isClearingRef/setTimeout) de la v7 par un
  // composant pleinement contrôlé.
  const rowSelectionState = useMemo(() => {
    const entries: [string, boolean][] = [];
    selectedRows.forEach((row) => {
      const id = getRowId(row);
      if (id !== undefined && id !== null) {
        entries.push([String(id), true]);
      }
    });
    return Object.fromEntries(entries);
  }, [selectedRows]);

  const handleRowSelectionChange = useCallback(
    (updater: any) => {
      if (!setSelectedRows) return;
      const nextState =
        typeof updater === "function" ? updater(rowSelectionState) : updater;
      const nextIds = new Set(
        Object.keys(nextState).filter((id) => nextState[id])
      );

      setSelectedRows((prev: any[]) => {
        const prevIds = new Set(
          prev.map((row) => getRowId(row)).filter((id) => id !== undefined)
        );
        const kept = prev.filter((row) => {
          const id = getRowId(row);
          return id === undefined || nextIds.has(String(id));
        });
        const added = (data || []).filter((row: any) => {
          const id = getRowId(row);
          return (
            id !== undefined && nextIds.has(String(id)) && !prevIds.has(id)
          );
        });
        return [...kept, ...added];
      });
    },
    [rowSelectionState, setSelectedRows, data]
  );

  const handlePaginationChange = useCallback(
    (updater: any) => {
      const current = { pageIndex: pageIndex ?? 0, pageSize: 10 };
      const next = typeof updater === "function" ? updater(current) : updater;
      if (next.pageIndex !== current.pageIndex) {
        setPageIndex(next.pageIndex);
      }
    },
    [pageIndex, setPageIndex]
  );

  const table = useTable({
    features,
    columns: finalColumns,
    data,
    getRowId: tableGetRowId,
    getRowCanExpand: () => true,
    manualPagination: true,
    pageCount: controlledPageCount,
    autoResetPageIndex: false,
    enableRowRangeSelection: false,
    state: {
      pagination: { pageIndex: pageIndex ?? 0, pageSize: 10 },
      rowSelection: rowSelectionState,
    },
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
  } as any);

  const classes = useStyles();

  const [showFilters, setShowFilters] = useState(true);

  const [persistedPagination, setPersistedPagination] = usePersistedState(
    0,
    `${pageName}Page-pagination`,
    null
  );

  const handleChangeFilter = useCallback(
    (name, value) => {
      // TODO ca va faire deux fetchData, mais super compliqué a optimiser,
      // il faut un debounce mais aussi modifier le fetchData pour que la fonction ne change jamais
      setPersistedPagination(0);
      table.setPageIndex(0);
      setFilter?.(name, value);
    },
    [table, setFilter, setPersistedPagination]
  );

  useEffect(() => {
    // Problème déjà rencontré
    // On a un useEffect qui regarde la pagination persistante et qui change la valeur
    // si elle est différente du state local, sauf que dans le cas d'un tableau avec un sous-tableau,
    // on passe deux fois le même state pour la pagination à deux JTable différents sur une même page.
    // donc on vérifie que le tableau ne soit pas un sous tableau (d'où le !isSubTable) pour changer le state
    // Guard: during error states we sometimes get `pageCount=0`.
    // Syncing pageIndex back to a persisted value in that case can lead to
    // rapid page flipping + extra refetches (and an empty preview).
    if (
      controlledPageCount > 0 &&
      pageIndex !== persistedPagination &&
      !isSubTable
    ) {
      setPageIndex(persistedPagination);
    }
  }, [
    controlledPageCount,
    persistedPagination,
    pageIndex,
    setPageIndex,
    isSubTable,
  ]);

  // buttons pagination

  let buttons: any[];

  if (controlledPageCount <= 7) {
    buttons = Array.from({ length: controlledPageCount }).map(
      (l, index) => index
    );
  } else if (pageIndex < 4) {
    buttons = [
      ...Array.from({ length: 5 }).map((l, index) => index),
      -1,
      controlledPageCount - 1,
    ];
  } else if (pageIndex > controlledPageCount - 5) {
    buttons = [
      0,
      -1,
      ...Array.from({ length: 5 }).map(
        (l, index) => index + controlledPageCount - 5
      ),
    ];
  } else {
    buttons = [
      0,
      -1,
      pageIndex - 1,
      pageIndex,
      pageIndex + 1,
      -1,
      controlledPageCount - 1,
    ];
  }

  const rows = table.getRowModel().rows;

  return (
    <>
      <TableContainer
        className={
          !isSubTable
            ? clsx(classes.tableContainer)
            : clsx(classes.subTableContainer)
        }
        component={usePaper ? Paper : undefined}
      >
        <Table aria-label="custom pagination table">
          {!isSubTable && (
            <TableHead
              className={
                !isSubTable ? classes.tableTitle : classes.subTableTitle
              }
            >
              {table.getHeaderGroups().map((headerGroup, index) => (
                <TableRow
                  key={`header-${index}-${!isSubTable ? "main" : "secondary"}`}
                >
                  {headerGroup.headers.map((header: any, cindex) =>
                    header.column.columnDef.meta?.hidden ? null : (
                      <TableCell
                        key={`column-${cindex}-${
                          !isSubTable ? "main" : "secondary"
                        }`}
                        style={{
                          minWidth: header.column.columnDef.meta?.minWidth,
                          maxWidth: header.column.columnDef.meta?.maxWidth,
                          width: header.column.columnDef.meta?.width,
                        }}
                        className={clsx(classes.tableCellTopHead)}
                        padding="none"
                      >
                        <div
                          className={classes.tophead}
                          style={{
                            justifyContent:
                              !isSubTable &&
                              cindex + 1 === headerGroup.headers.length
                                ? "flex-end"
                                : "space-between",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {(isSubTable ||
                            cindex + 1 !== headerGroup.headers.length) && (
                            <span
                              className="th-label"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                              {header.column.getIsSorted() &&
                                (header.column.getIsSorted() === "desc" ? (
                                  <i className="ml fas fa-sort-down" />
                                ) : (
                                  <i className="ml fas fa-sort-up" />
                                ))}
                            </span>
                          )}

                          {!isSubTable &&
                            cindex + 1 === headerGroup.headers.length &&
                            !hideFilters && (
                              <Button
                                onClick={() => setShowFilters(!showFilters)}
                                variant="text"
                                style={{
                                  textTransform: "none",
                                  minWidth: 20,
                                  marginLeft: 4,
                                  border: "none",
                                  padding: 4,
                                  textAlign: "center",
                                  borderRadius: "100%",
                                  position: "relative",
                                  top: -2,
                                }}
                              >
                                {showFilters ? (
                                  <ArrowUpSLineIcon
                                    size={25}
                                    style={{ margin: 0 }}
                                  />
                                ) : (
                                  <ArrowDownSLineIcon
                                    size={25}
                                    style={{ margin: 0 }}
                                  />
                                )}
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))}
            </TableHead>
          )}

          {!isSubTable && showFilters && (
            <TableHead className={classes.tableSearch}>
              {table.getHeaderGroups().map((headerGroup, index) => (
                <TableRow key={`header-2-${index}`}>
                  {headerGroup.headers.map((header: any, cindex) =>
                    header.column.columnDef.meta?.hidden ? null : (
                      <TableCell
                        key={`column-2-${cindex}`}
                        style={{
                          minWidth: header.column.columnDef.meta?.minWidth,
                          maxWidth: header.column.columnDef.meta?.maxWidth,
                          width: header.column.columnDef.meta?.width,
                        }}
                        padding="none"
                      >
                        <div className={classes.subheadPaddingXs}>
                          {header.column.columnDef.meta?.filterName &&
                            (header.column.columnDef.meta?.filterComponent ? (
                              header.column.columnDef.meta.filterComponent({
                                value:
                                  filters[
                                    header.column.columnDef.meta.filterName
                                  ],
                                onChange: (value: any) =>
                                  header.column.columnDef.meta.customFormat
                                    ? handleChangeFilter(
                                        header.column.columnDef.meta.filterName,
                                        header.column.columnDef.meta.customFormat(
                                          value
                                        )
                                      )
                                    : handleChangeFilter(
                                        header.column.columnDef.meta.filterName,
                                        value
                                      ),
                              })
                            ) : (
                              <JDebounceInput
                                className="input is-filter"
                                value={
                                  filters[
                                    header.column.columnDef.meta.filterName
                                  ]
                                }
                                onChange={(value: any) =>
                                  handleChangeFilter(
                                    header.column.columnDef.meta.filterName,
                                    value
                                  )
                                }
                              />
                            ))}
                        </div>
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))}
            </TableHead>
          )}
          {!children && (
            <TableBody>
              {!hasActionRow && data.length === 0 && isFetching && (
                <TableRow className={clsx(classes.tableRow)} key="loader">
                  <TableCell colSpan={100} className={clsx(classes.tableCell)}>
                    <div
                      className={classes.tdPaddingXs}
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      <JLoader isScreen permanant />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!hasActionRow && !isFetching && !rows.length && (
                <TableRow className={clsx(classes.tableRow)} key="no-result">
                  <TableCell colSpan={100} className={clsx(classes.tableCell)}>
                    <div
                      className={classes.tdPaddingXs}
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      <span className="table-no-result">
                        {filters && Object.keys(filters)?.length
                          ? noResultsText
                          : noDataText}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row: any, index) => {
                const isExpanded = row.getIsExpanded();
                return (
                  <React.Fragment key={index}>
                    <TableRow
                      {...rowProps(row)}
                      className={
                        !isExpanded
                          ? clsx(classes.tableRow)
                          : clsx(classes.tableRowExpanded)
                      }
                      key={`row-${index}${isExpanded ? "-e-" : ""}-${
                        !isSubTable ? "main" : "secondary"
                      }`}
                      data-tr={row.name}
                    >
                      {row.getAllCells().map((cell: any, cellIndex: number) =>
                        cell.column.columnDef.meta?.hidden ? null : (
                          <TableCell
                            padding="none"
                            key={`cell-${cellIndex}-${
                              !isSubTable ? "main" : "secondary"
                            }`}
                            data-cy={row.name}
                            data-row={cell.column.columnDef.meta?.name}
                            onClick={
                              !cell.column.columnDef.meta?.preventClickAction
                                ? () => {
                                    onClickAction();
                                    if (setSelectedItem) {
                                      setSelectedItem(row.original);
                                    }
                                  }
                                : undefined
                            }
                            style={{
                              minWidth: cell.column.columnDef.meta?.minWidth,
                              maxWidth: cell.column.columnDef.meta?.maxWidth,
                              width: cell.column.columnDef.meta?.width,
                            }}
                            className={clsx(classes.tableCell)}
                          >
                            <div className={classes.tdPaddingXs}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          </TableCell>
                        )
                      )}
                    </TableRow>

                    {isExpanded ? (
                      <TableRow
                        className={clsx(classes.subTableRow)}
                        key={`row-2-${index}`}
                        data-tr={`${row.name}-2`}
                      >
                        <TableCell colSpan={100}>
                          {renderRowSubComponent({ row })}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })}
              {hasActionRow &&
                data.length === actionRowNumber &&
                isFetching && (
                  <TableRow className={clsx(classes.tableRow)} key="loader-2">
                    <TableCell
                      colSpan={100}
                      className={clsx(classes.tableCell)}
                    >
                      <div
                        className={classes.tdPaddingXs}
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <JLoader isScreen permanant />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {hasActionRow &&
                !isFetching &&
                data.length === actionRowNumber && (
                  <TableRow
                    className={clsx(classes.tableRow)}
                    key="no-result-2"
                  >
                    <TableCell
                      colSpan={100}
                      className={clsx(classes.tableCell)}
                    >
                      <div
                        className={classes.tdPaddingXs}
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <span className="table-no-result">
                          {filters && Object.keys(filters)?.length
                            ? noResultsText
                            : noDataText}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          )}
        </Table>
        {children}
      </TableContainer>

      {!isSubTable && rows.length > 0 && controlledPageCount !== 1 && (
        <div className={classes.paginationFooter}>
          <IconButton
            onClick={() => {
              table.previousPage();
              setPersistedPagination(pageIndex - 1);
            }}
            disabled={!table.getCanPreviousPage() || isFetching}
            aria-label="previous page"
          >
            <KeyboardArrowLeft />
          </IconButton>

          {buttons.map((i, index) => {
            return i === -1 ? (
              <span key={`page-ellipsis-${i}-${index}`}>...</span>
            ) : (
              <Button
                key={`page-${i}-${index}`}
                disabled={isFetching}
                onClick={() => {
                  table.setPageIndex(i);
                  setPersistedPagination(i);
                }}
                className={`${pageIndex !== i && "active"} page`}
              >
                <span
                  style={{
                    padding: `0 ${(i + 1).toString().length >= 3 ? 4 : 0}px`,
                  }}
                >
                  {i + 1}
                </span>
              </Button>
            );
          })}

          <IconButton
            onClick={() => {
              table.nextPage();
              setPersistedPagination(pageIndex + 1);
            }}
            disabled={!table.getCanNextPage() || isFetching}
            aria-label="next page"
          >
            <KeyboardArrowRight />
          </IconButton>
        </div>
      )}
      {rowsPerPage && (
        <div style={{ float: "right" }}>
          <RowsPerPage
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            label="OP par page"
          />
        </div>
      )}
    </>
  );
}

const useStyles = createUseStyles(() => ({
  tableContainer: {
    padding: 16,
    width: "auto !important",
    overflowX: "auto !important",
    "& td.disabled, & td.disabled a": {
      color: "#B3BAC5 !important",
    },
    flex: "1",
    flexDirection: "column",
    "& td:last-child, & th:last-child": {
      position: "sticky",
      right: 0,
      // borderLeft: "2px solid #ddd",
    },
    "& th:last-child": {
      backgroundColor: "#F4F5F7",
    },
    "& td:last-child": {
      backgroundColor: "#ffffff",
    },
    "& tr:hover > td:last-child": {
      backgroundColor: "#F4F5F7",
    },
  },
  subTableContainer: {
    width: "auto !important",
    overflowX: "auto !important",
    minHeight: "auto !important",
    "& td.disabled, & td.disabled a": {
      color: "#B3BAC5 !important",
    },
    flex: "1",
    backgroundColor: "#F4F5F7",
  },
  tableTitle: {
    textTransform: "uppercase",
    backgroundColor: "#F4F5F7",
    borderRadius: "3px 3px 0px 0px",
    "& th": {
      color: "#7A869A",
      fontFamily: "Work Sans, sans-serif",
    },
    "& th, td": {
      lineHeight: "16px",
      padding: "4px 12px 0px 12px !important",
      fontSize: 13,
    },
  },
  subTableTitle: {
    backgroundColor: "#f4f5f7",
    "& th": {
      color: "#5c677a",
    },
    "& th, td": {
      lineHeight: "18px",
      padding: "4px 12px 0px 12px !important",
    },
  },
  tableSearch: {
    backgroundColor: "#F4F5F7",
    top: "44px !important",
    "& th, td": {
      // padding: '0px 18px 9px 18px !important',
      padding: "0px 12px 9px 12px !important",
      // '& div[class*="select-"]': {
      backgroundColor: "#f4f5f7 !important",
      // },
    },
  },
  tableLink: {
    color: "#030F85",
    fontWeight: "bold",
    textDecoration: "none",
  },
  tableFooter: {
    position: "sticky",
    left: 0,
    bottom: 0,
    "& td": {
      border: "none !important",
      paddingBottom: 7,
      paddingtop: 20,
      paddingLeft: 0,
    },
    "& tr": {
      border: "none",
    },
  },
  tableCellHead: {
    "&.MuiTableCell-root": {
      fontFamily: theme.font.workSans,
      // wordBreak: "break-word",
    },
  },
  tableCell: {
    "&.MuiTableCell-root": {
      fontFamily: theme.font.workSans,
      // wordBreak: "break-word",
    },
  },
  tableRow: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
    "&:hover": {
      backgroundColor: "#F4F5F7",
      '& div[class*="select-"]': {
        backgroundColor: "#f4f5f7 !important",
      },
    },
  },
  tableRowExpanded: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
    "& > td": {
      backgroundColor: "#e4e5e8",
      border: "none",
    },
  },
  subTableRow: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
    "& > td": {
      padding: "0 !important",
      border: "none",
      "& tr > td:first-child": {
        paddingLeft: "55px !important",
      },
      "& tr > th:first-child": {
        paddingLeft: "65px !important",
      },
    },
    "& td": {
      padding: "0",
    },
    '& td > div[class*="tdPadding"]': {
      minHeight: 35,
    },
  },
  CircularProgress: {
    display: "flex",
    justifyContent: "center",
    marginTop: 10,
  },
  BodyMissingTextContainer: {
    textAlign: "center",
    padding: "15px",
  },
  relative: {
    position: "relative",
  },
  tableCellTopHead: {
    borderBottom: "none !important;",
  },
  tdPaddingXs: {
    // padding: '4px 18px',
    padding: "0px 12px",
    minHeight: 50,
    display: "flex",
    alignItems: "center",
    wordBreak: "break-word",
    wordWrap: "break-word",
  },
  tophead: {
    minHeight: 40,
  },
  subheadPaddingXs: {
    padding: 0,
  },
  paginationFooter: {
    display: "flex",
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
    paddingTop: 16,
    paddingBottom: 16,
    textAlign: "right",
    "& button": {
      fontSize: 14,
      fontWeight: "bold",
      minWidth: 24,
      padding: 0,
      margin: "0 4px",
      "&.page": {
        padding: 0,
      },
      "&.active": {
        color: "#457DD9",
        background: "#457DD926 0% 0% no-repeat padding-box",
      },
      "&:hover": {
        color: "#457DD9",
        background: "#457DD926 0% 0% no-repeat padding-box",
      },
    },
  },
}));

export default JTable;
